// The Bio's career strip on ?live=1 (Ben, 2026-10-19): `/player-career` (one call — every
// competition + season + club, with appearances and minutes) → one card per season. Catalyst's
// "Appearance History" pop-up, as an always-visible timeline whose seasons drag onto other modules.

import type { PlayerCareerResponse, PlayerCareerRow } from "@catbento/shared";
import type { PlayerId } from "../data/players.js";
import type { LiveApi } from "./api.js";
import { humanizeSeasonCode } from "../modules/league-table-view.js";
import { withoutCountry } from "../modules/CountryFlag.js";
import type { LiveLeagueChoice } from "./league.js";

export function loadPlayerCareer(api: LiveApi, playerId: PlayerId): Promise<PlayerCareerResponse> {
  return api.get<PlayerCareerResponse>(`/player-career?playerId=${playerId}`);
}

/** What the strip knows of a competition: its label and flag (the League Table's own list). */
export interface CareerLeague {
  label: string;
  countryName: string | null;
  countryCode: string | null;
}

/** One line of a season's detail: a competition + club. */
export interface CareerLine extends PlayerCareerRow {
  league: CareerLeague | null;
}

/** One season. `main` = the club competition she played most minutes in — normally her league. */
export interface CareerSeason {
  seasonId: number;
  seasonCode: string;
  label: string;
  main: CareerLine;
  lines: CareerLine[];
  appearances: number;
  minutes: number;
  /** Her club changed from the season before (on the main line). */
  moved: boolean;
}

/** What `careerSeasons` needs from the league list: each competition's label + flag, and every
 *  country's name (to tell a national team from a club). Pure. */
export function careerLookups(leagues: readonly LiveLeagueChoice[]): {
  leagueOf: (competitionId: number) => CareerLeague | null;
  countries: ReadonlySet<string>;
} {
  const countries = new Set(leagues.flatMap((l) => (l.countryName ? [l.countryName.toLowerCase()] : [])));
  const leagueOf = (id: number) => {
    const l = leagues.find((c) => c.id === id);
    return l ? { label: withoutCountry(l.label, l.countryName), countryName: l.countryName, countryCode: l.countryCode } : null;
  };
  return { leagueOf, countries };
}

/** A national team's game: its team is a country ("England", "England Women", "England U21").
 *  Not "a competition with no country": the Champions League has none and is club football. Pure. */
export function isInternational(line: CareerLine, countries: ReadonlySet<string>): boolean {
  const team = line.teamName.replace(/\s+(women|u-?\d{2}|under[- ]?\d{2}|olympic|b)$/i, "").trim().toLowerCase();
  return countries.has(team);
}

/** Her CLUB career, one entry per season, oldest first (Ben, 2026-10-19: club career only).
 *  National-team games are left out altogether — they're filed under calendar years ("2024") and
 *  our data has only some of them, so they'd mislead. `countries` = every country's name. Pure. */
export function careerSeasons(
  rows: readonly PlayerCareerRow[],
  leagueOf: (competitionId: number) => CareerLeague | null,
  countries: ReadonlySet<string>,
  /** The national-team side instead (the Career screen's International list, Ben 2026-10-19). */
  international = false,
): CareerSeason[] {
  const bySeason = new Map<number, CareerLine[]>();
  for (const r of rows) {
    const line = { ...r, league: leagueOf(r.competitionId) };
    if (isInternational(line, countries) !== international) continue;
    bySeason.set(r.seasonId, [...(bySeason.get(r.seasonId) ?? []), line]);
  }
  const seasons = [...bySeason.entries()].map(([seasonId, lines]) => {
    const byMinutes = [...lines].sort((a, b) => b.minutes - a.minutes);
    const main = byMinutes[0]!;
    const last = lines.reduce((d, l) => (l.lastMatchDate > d ? l.lastMatchDate : d), "");
    return {
      seasonId,
      seasonCode: String(seasonId),
      label: humanizeSeasonCode(String(seasonId)),
      main,
      lines: byMinutes,
      appearances: lines.reduce((t, l) => t + l.appearances, 0),
      minutes: lines.reduce((t, l) => t + l.minutes, 0),
      moved: false,
      last,
    };
  });
  seasons.sort((a, b) => (a.last < b.last ? -1 : a.last > b.last ? 1 : a.seasonId - b.seasonId));
  return seasons.map((s, i) => ({
    seasonId: s.seasonId,
    seasonCode: s.seasonCode,
    label: s.label,
    main: s.main,
    lines: s.lines,
    appearances: s.appearances,
    minutes: s.minutes,
    moved: i > 0 && seasons[i - 1]!.main.teamId !== s.main.teamId,
  }));
}
